import type { ReactNode } from 'react';
import { CommandGroup, CommandItem } from '../ui/command';
import { MentionRow, type MentionRowProps } from './MentionRow';

export interface MentionCommandItemProps extends MentionRowProps {
  /** cmdk item value — keep unique across the flattened mention menu. */
  value: string;
  /** Extra tokens cmdk can match besides `value`. */
  keywords?: string[];
  onSelect?: () => void;
  disabled?: boolean;
  /**
   * Extra classes on the `CommandItem`. {@link MentionRowProps.className}
   * still styles the name span.
   */
  itemClassName?: string;
  /** Stable hook for tests; also carried as `data-testid` on the item. */
  testId?: string;
}

/**
 * One selectable `@`-mention result: a `CommandItem` whose child is always
 * {@link MentionRow}, so every provider's rows share one layout.
 */
export function MentionCommandItem({
  icon,
  name,
  details,
  action,
  className,
  value,
  keywords,
  onSelect,
  disabled,
  itemClassName,
  testId,
}: MentionCommandItemProps) {
  return (
    <CommandItem
      value={value}
      keywords={keywords}
      onSelect={onSelect}
      disabled={disabled}
      className={itemClassName}
      data-testid={testId}
    >
      <MentionRow icon={icon} name={name} details={details} action={action} className={className} />
    </CommandItem>
  );
}

export interface MentionCommandListProps {
  /** When set, wraps children in a `CommandGroup` with this heading. */
  groupHeading?: string;
  children: ReactNode;
}

/**
 * Optional `CommandGroup` wrapper used by mention providers in the flattened
 * root search. Scoped views pass no heading and render a fragment.
 */
export function MentionCommandList({ groupHeading, children }: MentionCommandListProps) {
  if (groupHeading) {
    return <CommandGroup heading={groupHeading}>{children}</CommandGroup>;
  }
  return <>{children}</>;
}
